import * as Dfns from 'date-fns/fp';
import { useQuery } from '@tanstack/react-query';
import { awaitJSON, LinearLambdaFetch } from '@src/utils/fetch-utils';
import { DATE_FORMAT } from './linear-pacing-constants';
import { ClearanceRow } from './linear-pacing-utils';

interface ClearanceResponse {
  data: ClearanceRow[];
}

type ClearanceLevel = 'company' | 'network' | 'avail' | 'rotation';

interface ClearanceParams {
  group: ClearanceLevel | ClearanceLevel[];
  start: string;
  end: string;
  network?: string;
  avail?: string;
}

// Every Monday from `start` through `end` (both yyyy-MM-dd), used to pad each clearance series out
// to one point per week.
export const buildWeekRange = (start: string, end: string): string[] => {
  const endDate = Dfns.parseISO(end);
  let current = Dfns.parseISO(start);
  const range = [Dfns.format(DATE_FORMAT, current)];
  while (Dfns.isBefore(endDate, current)) {
    current = Dfns.addWeeks(1, current);
    range.push(Dfns.format(DATE_FORMAT, current));
  }
  return range;
};

const fetchClearance = async ({ network, avail, ...rest }: ClearanceParams) => {
  const res = await LinearLambdaFetch('/clearance', {
    params: { ...rest, ...(network && { network }), ...(avail && { avail }) }
  });
  const { data } = await awaitJSON<ClearanceResponse>(res);
  return data;
};

export const CLEARANCE_QUERY_KEY = 'linearClearance';
export const FORECAST_QUERY_KEY = 'linearClearanceForecast';

// Weekly clearance rows for one drill level; only runs once `enabled` (its parent selection
// exists).
export const useClearance = (params: ClearanceParams, enabled = true) =>
  useQuery({
    queryKey: [CLEARANCE_QUERY_KEY, params],
    enabled: enabled && Boolean(params.start && params.end),
    // The clearance and forecast tabs raise their own error dialog.
    meta: { suppressGlobalError: true },
    queryFn: () => fetchClearance(params)
  });

export const useClearanceForecast = (start: string, end: string) =>
  useQuery({
    queryKey: [FORECAST_QUERY_KEY, start, end],
    enabled: Boolean(start && end),
    meta: { suppressGlobalError: true },
    queryFn: async () => {
      const res = await LinearLambdaFetch('/clearance/forecast', { params: { start, end } });
      const { data } = await awaitJSON<ClearanceResponse>(res);
      return data;
    }
  });
